import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup, FormLabel, Switch, TextField } from '@mui/material';
import { RootState } from '../../reducers';
import { useAppDispatch } from '../../store/store';
import { createModifier, updateModifier } from '../../store/adminCPTModifiersSlice.slice';
import ICPTModifier from '../../Models/Shared/CPTModifier.model';

interface Props {
    open: boolean;
    onClose: () => void;
}

const AdminCPTModifierForm = ({ open, onClose }: Props) => {
    const dispatch = useAppDispatch();
    const { selectedModifier } = useSelector((state: RootState) => state.adminCPTModifiersSlice);

    const [code, setCode] = useState('');
    const [description, setDescription] = useState('');
    const [pos11, setPos11] = useState(false);
    const [pos12, setPos12] = useState(false);
    const [isActive, setIsActive] = useState(true);

    useEffect(() => {
        if (selectedModifier) {
            setCode(selectedModifier.Code ?? '');
            setDescription(selectedModifier.Description ?? '');
            setPos11(selectedModifier.ApplicablePOSValues?.includes(11) ?? false);
            setPos12(selectedModifier.ApplicablePOSValues?.includes(12) ?? false);
            setIsActive(selectedModifier.IsActive ?? true);
        }
    }, [selectedModifier]);

    const handleSave = () => {
        const applicablePOSValues: number[] = [];
        if (pos11) applicablePOSValues.push(11);
        if (pos12) applicablePOSValues.push(12);

        const modifier: ICPTModifier = {
            Id: selectedModifier?.Id ?? '',
            Code: code.toUpperCase().trim(),
            Description: description.trim(),
            ApplicablePOSValues: applicablePOSValues,
            IsActive: isActive,
        };

        if (modifier.Id) {
            dispatch(updateModifier(modifier));
        } else {
            dispatch(createModifier(modifier));
        }
    };

    const isNew = !selectedModifier?.Id;
    const isValid = code.trim().length > 0 && code.trim().length <= 2 && description.trim().length > 0 && (pos11 || pos12);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isNew ? 'Add Modifier' : 'Edit Modifier'}</DialogTitle>
            <DialogContent>
                <TextField
                    label="Code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    inputProps={{ maxLength: 2 }}
                    fullWidth
                    size="small"
                    sx={{ mt: 2, mb: 2 }}
                    helperText="2-character modifier code (e.g. HN, GT, 95)"
                />
                <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth size="small" sx={{ mb: 2 }} />
                <FormLabel component="legend" sx={{ mb: 1 }}>
                    Applicable Place of Service
                </FormLabel>
                <FormGroup row sx={{ mb: 2 }}>
                    <FormControlLabel control={<Checkbox checked={pos11} onChange={(e) => setPos11(e.target.checked)} />} label="11 — Office" />
                    <FormControlLabel control={<Checkbox checked={pos12} onChange={(e) => setPos12(e.target.checked)} />} label="12 — Home" />
                </FormGroup>
                <FormControlLabel control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />} label="Active" />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={!isValid}>
                    {isNew ? 'Create' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AdminCPTModifierForm;
